import {
  Bell,
  CircleAlert,
  TriangleAlert,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge } from '@invento/ui/components/badge';
import { Button } from '@invento/ui/components/button';
import { DataState } from '@invento/ui/components/data-state';
import { HeaderBackButton } from '@invento/ui/components/header-back-button';
import type { BadgeVariant } from '@invento/ui/components/badge';
import { ROUTE_PATHS } from '@/navigation/constants';
import { useHasPermission } from '@/core/auth/permissions';
import { useAuthStore } from '@/state/stores/auth.store';
import {
  useArchiveNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationCenterList,
  useNotificationUnreadCount,
} from '../hooks/use-notification-center';
import { notificationDisplayCopy, notificationSeverityLabel } from '../helpers/notification-copy';
import { SALES_PERMISSIONS, type NotificationCenterItemDto } from '@invento/shared';

const NOTIFICATION_PAGE_SIZE = 10;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TEXT_ACTION_CLASS_NAME =
  'min-h-11 rounded-md border-0 bg-transparent px-1 text-[12px] font-semibold text-[var(--a-primary-text)] underline underline-offset-4 transition-colors hover:bg-[var(--a-primary-soft)] hover:text-[var(--a-primary-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--a-primary)] disabled:cursor-not-allowed disabled:text-[var(--a-text-faint)] sm:min-h-8';

interface NotificationCenterPanelProps {
  onClose?: () => void;
  onNavigateAway?: () => void;
}

export function NotificationCenterPanel({ onClose, onNavigateAway }: NotificationCenterPanelProps) {
  const { t, i18n } = useTranslation('notifications', { useSuspense: false });
  const user = useAuthStore((state) => state.user);
  const enabled = Boolean(user);
  const unreadCountQuery = useNotificationUnreadCount(enabled);
  const listQuery = useNotificationCenterList({
    limit: NOTIFICATION_PAGE_SIZE,
    includeArchived: false,
    enabled,
  });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const archive = useArchiveNotification();
  const [selectedNotification, setSelectedNotification] = useState<NotificationCenterItemDto | null>(null);
  const [lastViewedNotificationId, setLastViewedNotificationId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState(false);
  const detailBackButtonRef = useRef<HTMLButtonElement | null>(null);
  const detailTriggerRef = useRef<HTMLButtonElement | null>(null);

  const unreadCount = unreadCountQuery.data?.data.unreadCount ?? 0;
  const notifications = listQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const isInitialLoading = listQuery.isLoading && notifications.length === 0;
  const isMutating = markRead.isPending || markAllRead.isPending || archive.isPending;
  const notificationGroups = useMemo(
    () => groupNotificationsByDay(notifications, i18n.resolvedLanguage || i18n.language || 'en', {
      today: t('groups.today', { defaultValue: 'Today' }),
      yesterday: t('groups.yesterday', { defaultValue: 'Yesterday' }),
    }),
    [i18n.language, i18n.resolvedLanguage, notifications, t],
  );

  useEffect(() => {
    if (!selectedNotification) return;
    const frame = requestAnimationFrame(() => detailBackButtonRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [selectedNotification]);

  function openNotificationDetail(
    notification: NotificationCenterItemDto,
    event: ReactMouseEvent<HTMLButtonElement>,
  ) {
    detailTriggerRef.current = event.currentTarget;
    setLastViewedNotificationId(notification.id);
    setSelectedNotification(notification);
  }

  function closeNotificationDetail() {
    setSelectedNotification(null);
    requestAnimationFrame(() => detailTriggerRef.current?.focus());
  }

  function markNotificationRead(notificationId: string) {
    setMutationError(false);
    markRead.mutate(notificationId, {
      onError: () => setMutationError(true),
      onSuccess: () => setMutationError(false),
    });
  }

  function markAllNotificationsRead() {
    setMutationError(false);
    markAllRead.mutate(undefined, {
      onError: () => setMutationError(true),
      onSuccess: () => setMutationError(false),
    });
  }

  function archiveNotification(notificationId: string) {
    setMutationError(false);
    archive.mutate(notificationId, {
      onError: () => setMutationError(true),
      onSuccess: () => setMutationError(false),
    });
  }

  if (!enabled) return null;

  return (
    <section
      aria-label={t('title', 'Notifications')}
      aria-modal="false"
      aria-busy={isInitialLoading || isMutating}
      role="dialog"
      tabIndex={-1}
      className="w-full overflow-hidden rounded-2xl border border-[var(--a-line)] bg-[var(--a-surface)] text-[var(--a-text)] shadow-[0_18px_42px_rgba(0,0,0,0.16)] [font-family:var(--a-body)]"
    >
      {selectedNotification ? (
        <NotificationDetail
          backButtonRef={detailBackButtonRef}
          locale={i18n.resolvedLanguage || i18n.language || 'en'}
          notification={selectedNotification}
          onBack={closeNotificationDetail}
          onClose={onClose}
          onNavigateAway={onNavigateAway}
        />
      ) : (
        <>
          <div className="flex items-start justify-between gap-3 border-b border-[var(--a-line)] px-4 py-3.5">
            <div className="min-w-0 flex-1">
              <h2 className="m-0 text-[15px] font-semibold tracking-[-0.01em] text-[var(--a-text)]">
                {t('title', 'Notifications')}
              </h2>
              <p aria-live="polite" className="m-0 mt-0.5 text-[12px] text-[var(--a-text-soft)]">
                {unreadCount > 0
                  ? t('summaryUnread', { count: unreadCount, defaultValue: '{{count}} unread' })
                  : t('summaryRead', { defaultValue: 'All caught up' })}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                disabled={unreadCount === 0 || markAllRead.isPending || isMutating}
                onClick={markAllNotificationsRead}
                className={TEXT_ACTION_CLASS_NAME}
              >
                {t('actions.markAllRead', 'Mark all read')}
              </button>
              {onClose ? (
                <Button
                  type="button"
                  size="dense"
                  variant="ghost"
                  aria-label={t('actions.close', 'Close notifications')}
                  data-notification-close
                  className="!h-8 !w-8 !px-0"
                  onClick={onClose}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              ) : null}
            </div>
          </div>

          {mutationError ? (
            <p
              role="alert"
              className="m-3 mb-0 rounded-lg border border-[var(--a-danger-soft)] bg-[var(--a-danger-soft)] px-3 py-2 text-[12px] leading-5 text-[var(--a-danger)]"
            >
              {t(
                'error.updateFailed',
                'The notification update did not complete. The notification remains unchanged. Please try again.',
              )}
            </p>
          ) : null}

          <div className="max-h-[min(70vh,32rem)] overflow-y-auto">
            {isInitialLoading ? (
              <div className="p-3">
                <DataState
                  title={t('loading.title', 'Loading notifications')}
                  description={t('loading.description', 'Checking your latest workspace notifications.')}
                  tone="neutral"
                  className="border-dashed bg-[var(--a-surface)] p-4"
                />
              </div>
            ) : null}

            {listQuery.isError ? (
              <div className="p-3">
                <DataState
                  title={t('error.title', 'Notifications unavailable')}
                  description={t('error.description', 'Refresh the page or try again shortly.')}
                  tone="danger"
                  className="p-4"
                />
              </div>
            ) : null}

            {!isInitialLoading && !listQuery.isError && notifications.length === 0 ? (
              <div className="p-3">
                <DataState
                  title={t('empty.title', 'No notifications')}
                  description={t('empty.description', 'Unread and active notifications will appear here.')}
                  tone="neutral"
                  className="border-dashed bg-[var(--a-surface)] p-4"
                />
              </div>
            ) : null}

            {!isInitialLoading && !listQuery.isError
              ? notificationGroups.map((group) => (
                  <section key={group.key} aria-label={group.label}>
                    <h3 className="m-0 border-b border-[var(--a-line-soft)] bg-[var(--a-surface-2)] px-4 py-2 text-[11.5px] font-medium text-[var(--a-text-soft)]">
                      {group.label}
                    </h3>
                    {group.notifications.map((notification) => (
                      <NotificationCenterRow
                        key={notification.id}
                        notification={notification}
                        locale={i18n.resolvedLanguage || i18n.language || 'en'}
                        onArchive={() => archiveNotification(notification.id)}
                        onMarkRead={() => markNotificationRead(notification.id)}
                        onView={(event) => openNotificationDetail(notification, event)}
                        viewButtonRef={lastViewedNotificationId === notification.id ? detailTriggerRef : undefined}
                        actionDisabled={isMutating}
                      />
                    ))}
                  </section>
                ))
              : null}
          </div>

          {listQuery.hasNextPage ? (
            <div className="border-t border-[var(--a-line)] p-3">
              <Button
                size="dense"
                variant="ghost"
                fullWidth
                disabled={listQuery.isFetchingNextPage}
                onClick={() => void listQuery.fetchNextPage()}
              >
                {listQuery.isFetchingNextPage ? t('actions.loadingMore', 'Loading more...') : t('actions.loadMore', 'Load more')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

interface NotificationCenterRowProps {
  actionDisabled: boolean;
  locale: string;
  notification: NotificationCenterItemDto;
  onArchive: () => void;
  onMarkRead: () => void;
  onView: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  viewButtonRef?: RefObject<HTMLButtonElement | null>;
}

function NotificationCenterRow({
  actionDisabled,
  locale,
  notification,
  onArchive,
  onMarkRead,
  onView,
  viewButtonRef,
}: NotificationCenterRowProps) {
  const { t } = useTranslation('notifications', { useSuspense: false });
  const copy = notificationDisplayCopy(notification, t);
  const unread = notification.readAt === null;
  const typeVisual = notificationTypeVisual(notification);
  const TypeIcon = typeVisual.Icon;
  const timestamp = new Date(notification.createdAt);

  return (
    <article className="border-b border-[var(--a-line-soft)] px-4 py-3.5 last:border-b-0">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={
            unread
              ? 'mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--a-primary)]'
              : 'mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-transparent'
          }
        />
        <span
          aria-hidden="true"
          data-notification-type-icon
          className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full ${typeVisual.className}`}
        >
          <TypeIcon className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <Badge size="sm" variant={notificationSeverityBadgeVariant(notification.severity)}>
              {notificationSeverityLabel(notification.severity, t)}
            </Badge>
            <time
              dateTime={notification.createdAt}
              className="text-[11.5px] leading-4 text-[var(--a-text-faint)]"
            >
              {notificationTime(timestamp, locale)}
            </time>
          </div>
          <h4 className="m-0 mt-1 min-w-0 break-words text-[13.5px] font-semibold leading-5 text-[var(--a-text)]">
            {copy.title}
          </h4>
          <p className="m-0 mt-0.5 break-words text-[12.5px] leading-5 text-[var(--a-text-soft)]">
            {copy.description}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <button ref={viewButtonRef} type="button" onClick={onView} className={TEXT_ACTION_CLASS_NAME}>
              {t('actions.view', 'View notification')}
            </button>
            {unread ? (
              <button type="button" disabled={actionDisabled} onClick={onMarkRead} className={TEXT_ACTION_CLASS_NAME}>
                {t('actions.markRead', 'Mark read')}
              </button>
            ) : null}
            <button
              type="button"
              disabled={actionDisabled}
              onClick={onArchive}
              className={TEXT_ACTION_CLASS_NAME}
            >
              {t('actions.archive', 'Archive')}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

interface NotificationDetailProps {
  backButtonRef: RefObject<HTMLButtonElement | null>;
  locale: string;
  notification: NotificationCenterItemDto;
  onBack: () => void;
  onClose?: () => void;
  onNavigateAway?: () => void;
}

function NotificationDetail({
  backButtonRef,
  locale,
  notification,
  onBack,
  onClose,
  onNavigateAway,
}: NotificationDetailProps) {
  const { t } = useTranslation('notifications', { useSuspense: false });
  const canReadSales = useHasPermission(SALES_PERMISSIONS.READ);
  const copy = notificationDisplayCopy(notification, t);
  const recordRoute = canReadSales ? notificationRecordRoute(notification) : null;

  return (
    <section aria-labelledby="notification-detail-title" className="min-h-0">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--a-line)] px-4 py-3.5">
        <HeaderBackButton
          ref={backButtonRef}
          onClick={onBack}
          className="min-h-11 px-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--a-primary)] sm:min-h-8"
        >
          {t('actions.backToNotifications', 'Back to notifications')}
        </HeaderBackButton>
        {onClose ? (
          <Button
            type="button"
            size="dense"
            variant="ghost"
            aria-label={t('actions.close', 'Close notifications')}
            data-notification-close
            className="!h-8 !w-8 !px-0"
            onClick={onClose}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>

      <div className="max-h-[min(70vh,32rem)] overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge size="sm" variant={notificationSeverityBadgeVariant(notification.severity)}>
            {notificationSeverityLabel(notification.severity, t)}
          </Badge>
          <time dateTime={notification.createdAt} className="text-[12px] leading-5 text-[var(--a-text-faint)]">
            {notificationDetailTime(new Date(notification.createdAt), locale)}
          </time>
        </div>
        <h3 id="notification-detail-title" className="m-0 mt-2 break-words text-[18px] font-semibold leading-6 tracking-[-0.01em] text-[var(--a-text)]">
          {copy.title}
        </h3>
        <p className="m-0 mt-2 break-words text-[14px] leading-6 text-[var(--a-text-soft)]">{copy.description}</p>
        {recordRoute ? (
          <Link
            to={recordRoute}
            onClick={onNavigateAway}
            className="mt-5 inline-flex min-h-11 items-center justify-center rounded-lg border border-transparent bg-[var(--a-primary-action)] px-3.5 text-[13px] font-medium text-[var(--a-primary-action-fg)] transition-colors duration-[140ms] hover:bg-[var(--a-primary-action-hover)] focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-[var(--a-primary)] sm:min-h-9"
          >
            {notification.templateToken === 'sales.pos_shift_closed'
              ? t('actions.reviewShift', 'Review shift')
              : t('actions.viewSaleRecord', 'View sale record')}
          </Link>
        ) : null}
      </div>
    </section>
  );
}

function notificationRecordRoute(notification: NotificationCenterItemDto): string | null {
  if (
    notification.templateToken === 'sales.pos_shift_closed' &&
    notification.sourceType === 'pos_shift' &&
    UUID_PATTERN.test(notification.sourceId)
  ) {
    return ROUTE_PATHS.SALES.POS_SHIFT_REVIEWS;
  }
  if (
    notification.templateToken !== 'sales.large_sale' ||
    notification.sourceType !== 'sales.invoice' ||
    !UUID_PATTERN.test(notification.sourceId)
  ) {
    return null;
  }

  return ROUTE_PATHS.SALES.INVOICE_DETAIL(encodeURIComponent(notification.sourceId));
}

function groupNotificationsByDay(
  notifications: NotificationCenterItemDto[],
  locale: string,
  labels: { today: string; yesterday: string },
) {
  const today = calendarDayKey(new Date());
  const yesterday = calendarDayKey(new Date(Date.now() - 86_400_000));
  const groups = new Map<string, { key: string; label: string; notifications: NotificationCenterItemDto[] }>();

  notifications.forEach((notification) => {
    const date = new Date(notification.createdAt);
    const key = calendarDayKey(date);
    const label = key === today
      ? labels.today
      : key === yesterday
        ? labels.yesterday
        : new Intl.DateTimeFormat(locale || 'en', { month: 'short', day: 'numeric' }).format(date);
    const group = groups.get(key);
    if (group) {
      group.notifications.push(notification);
      return;
    }
    groups.set(key, { key, label, notifications: [notification] });
  });

  return Array.from(groups.values());
}

function calendarDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function notificationTime(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale || 'en', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function notificationDetailTime(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale || 'en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function notificationSeverityBadgeVariant(notificationSeverity: NotificationCenterItemDto['severity']): BadgeVariant {
  if (notificationSeverity === 'error') return 'error';
  if (notificationSeverity === 'warning') return 'warning';
  return 'info';
}

function notificationTypeVisual(notification: NotificationCenterItemDto): {
  Icon: LucideIcon;
  className: string;
} {
  if (notification.severity === 'error') {
    return { Icon: CircleAlert, className: 'bg-[var(--a-danger-soft)] text-[var(--a-danger)]' };
  }
  if (notification.severity === 'warning') {
    return { Icon: TriangleAlert, className: 'bg-[var(--a-warning-soft)] text-[var(--a-warning)]' };
  }
  return { Icon: Bell, className: 'bg-[var(--a-info-soft)] text-[var(--a-info)]' };
}
